import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, ExternalLink, Plus, Edit, Search, X, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { useToast } from "../../context/ToastContext";
import * as adminApi from "../../api/adminApi";
import { formatMoney } from "../../utils/format";
import { getProductDetailUrl } from "../../utils/productSlug";
import "../Auth.css";
import "./AdminProducts.css";

export default function AdminProducts() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", basePrice: "", code: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const data = await adminApi.fetchAdminProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch (e) {
      addToast(e.message || "Failed to load products.", "error");
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const remove = async (productId, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await adminApi.deleteAdminProduct(productId);
      addToast(`Product "${name}" deleted.`, "success");
      await load();
    } catch (e) {
      addToast(e.message || "Delete failed.", "error");
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const newProduct = await adminApi.createAdminProduct({
        name: form.name.trim(),
        description: form.description.trim(),
        basePrice: Number(form.basePrice),
        code: form.code.trim(),
      });
      setModal(false);
      setForm({ name: "", description: "", basePrice: "", code: "" });
      addToast("Product created.", "success");
      // Redirect to the product edit page
      navigate(`/admin/products/${newProduct.id}`);
    } catch (err) {
      addToast(err.message || "Could not create product.", "error");
    } finally {
      setSaving(false);
    }
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("name"); // "code" | "name" | "price"
  const [sortDirection, setSortDirection] = useState("asc"); // "asc" | "desc"

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const filteredAndSortedProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    let result = products;

    if (query) {
      result = result.filter((p) => {
        const code = (p.code || "").toLowerCase();
        const name = (p.name || "").toLowerCase();
        return code.includes(query) || name.includes(query);
      });
    }

    return [...result].sort((a, b) => {
      let comparison = 0;
      if (sortField === "code") {
        const codeA = (a.code || "").toLowerCase();
        const codeB = (b.code || "").toLowerCase();
        comparison = codeA.localeCompare(codeB);
      } else if (sortField === "price") {
        const priceA = Number(a.basePrice ?? 0);
        const priceB = Number(b.basePrice ?? 0);
        comparison = priceA - priceB;
      } else if (sortField === "name") {
        const nameA = (a.name || "").toLowerCase();
        const nameB = (b.name || "").toLowerCase();
        comparison = nameA.localeCompare(nameB);
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [products, searchQuery, sortField, sortDirection]);

  return (
    <div className="admin-products">
      <div className="admin-products__head">
        <div>
          <p className="section-label">Catalogue</p>
          <h2 className="admin-products__title">Products</h2>
        </div>
        <button type="button" className="btn-primary admin-products__add" onClick={() => setModal(true)}>
          <Plus size={15} /> Add product
        </button>
      </div>

      <div className="admin-products__toolbar">
        <div className="admin-products__search-box">
          <Search size={16} className="admin-products__search-icon" />
          <input
            type="text"
            className="admin-products__search-input"
            placeholder="Filter by code or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="admin-products__search-clear"
              onClick={() => setSearchQuery("")}
              aria-label="Clear filter"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="admin-products__count">
          Showing {filteredAndSortedProducts.length} of {products.length} products
        </div>
      </div>

      {loading ? (
        <div className="page-loading">
          <span className="page-loading__dot" />
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th
                  className="admin-table__sortable-th"
                  onClick={() => handleSort("code")}
                  title="Sort by code"
                >
                  <span className="admin-table__th-content">
                    Code
                    <span className="admin-table__sort-icon">
                      {sortField === "code" ? (
                        sortDirection === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      ) : (
                        <ArrowUpDown size={14} />
                      )}
                    </span>
                  </span>
                </th>
                <th
                  className="admin-table__sortable-th"
                  onClick={() => handleSort("name")}
                  title="Sort by name"
                >
                  <span className="admin-table__th-content">
                    Name
                    <span className="admin-table__sort-icon">
                      {sortField === "name" ? (
                        sortDirection === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      ) : (
                        <ArrowUpDown size={14} />
                      )}
                    </span>
                  </span>
                </th>
                <th
                  className="admin-table__sortable-th"
                  onClick={() => handleSort("price")}
                  title="Sort by price"
                >
                  <span className="admin-table__th-content">
                    Price
                    <span className="admin-table__sort-icon">
                      {sortField === "price" ? (
                        sortDirection === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      ) : (
                        <ArrowUpDown size={14} />
                      )}
                    </span>
                  </span>
                </th>
                <th className="admin-table__actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedProducts.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", padding: "32px", color: "var(--text-muted)" }}>
                    No products match your filter.
                  </td>
                </tr>
              ) : (
                filteredAndSortedProducts.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <code style={{ fontSize: "12px", fontFamily: "monospace", color: "var(--text-muted)" }}>
                        {p.code || "-"}
                      </code>
                    </td>
                    <td>
                      <strong>{p.name}</strong>
                      <p className="admin-products__desc">{p.description?.slice(0, 80)}{p.description?.length > 80 ? "…" : ""}</p>
                    </td>
                    <td>{formatMoney(p.basePrice)}</td>
                    <td className="admin-table__actions">
                      <div className="admin-products__row-actions">
                        <Link to={`/admin/products/${p.id}`} className="admin-icon-link" title="Edit product">
                          <Edit size={16} />
                        </Link>
                        <Link to={getProductDetailUrl(p)} state={{ productId: p.id }} target="_blank" rel="noopener noreferrer" className="admin-icon-link" title="View on store">
                          <ExternalLink size={16} />
                        </Link>
                        <button
                          type="button"
                          className="admin-icon-link admin-icon-link--danger"
                          title="Delete"
                          onClick={() => remove(p.id, p.name)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="admin-modal-overlay" role="dialog">
          <div className="admin-modal">
            <h3>New product</h3>
            <p className="admin-modal__hint">Creates basic info; add images & variants in the API or future editor.</p>
            <form onSubmit={submitCreate}>
              <div className="auth-field">
                <label>Code (SKU)</label>
                <input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} required />
              </div>
              <div className="auth-field">
                <label>Name</label>
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="auth-field">
                <label>Description</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  required
                />
              </div>
              <div className="auth-field">
                <label>Base price</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.basePrice}
                  onChange={(e) => setForm((f) => ({ ...f, basePrice: e.target.value }))}
                  required
                />
              </div>
              <div className="admin-modal__actions">
                <button type="button" className="btn-outline" onClick={() => setModal(false)}>
                  Close
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving…" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
